// Sheet full-screen para convertir una IMAGEN GENERADA en un anuncio.
// Se abre desde la action bar (menú "Más") de GeneratedImagesInline.
//
// Reusa la UI del modo anuncio (`AnuncioPanel`: textos + fuente, colores,
// formato, cantidad, instrucción) PERO:
//   • sin la sección "Productos" — la base es la imagen generada, no un
//     producto (AnuncioPanel omite esa sección si no recibe `products`).
//   • la imagen base se muestra grande: en desktop a la IZQUIERDA de las
//     opciones, en mobile ARRIBA.
//
// Generación: `postGenerateAd` contra el endpoint V3 (rutea a atlas-backend
// o n8n según flag/modo — ver generateAdRequest) con
// `modo: 'anuncio_desde_imagen'` y `imagen_generada_id` (en vez de
// `variante_id`). El backend usa esa imagen como referencia. Corre con
// carrera respuesta-vs-poll por `execution_id` (ADR-577).

import React, { useEffect, useRef, useState } from 'react';
import { sonarListo } from '../utils/sonarListo';
import { createPortal } from 'react-dom';
import { ChevronLeft, Sparkles } from 'lucide-react';
import {
  AnuncioPanel,
  ANUNCIO_COLORS_MAX,
  normalizeBrandHexColors,
  resolveBrandFont,
  type AnuncioFieldState,
} from '../pages/v2/panels/AnuncioPanel';
import { FormatPicker } from '../pages/v2/FormatPicker';
import { getFormatLabel } from '../pages/v2/mockData';
import LoadingAnimation from './LoadingAnimation';
import { postGenerateAd, GenerateAdError } from '../lib/generateAdRequest';
import { awaitImageGeneration } from '../pages/v2/imageGenerationRecovery';
import {
  ANUNCIO_POLL_TIMEOUT_MS,
  ANUNCIO_STABLE_ROUNDS,
} from '../pages/v2/pollGeneratedByExecutionId';
import { GENERATE_AD_V3_URL } from '../config';
import { preloadGeneratedImages } from '../utils/preloadGeneratedImages';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';

const AUTO_IMAGE_COUNT_MAX = 6;
const FIELD_DEFAULT: AnuncioFieldState = { hidden: false, value: '', font: '' };

// Mismo mapeo slug → aspect ratio que InterfazV2 usa para el modo anuncio.
const formatSlugToAspect = (slug: string | null): string => {
  switch (slug) {
    case 'story':
      return '9:16';
    case 'square':
      return '1:1';
    case 'portrait':
      return '2:3';
    case 'banner':
      return '16:9';
    case 'editorial':
      return '3:2';
    case 'billboard':
      return '21:9';
    case 'post':
    default:
      return '4:5';
  }
};

export interface AnuncioFromImageSheetProps {
  open: boolean;
  onClose: () => void;
  /** Imagen generada base: `id` para el backend + `url` (firmada) para mostrar. */
  image: { id: string; url: string } | null;
  companyId: string;
  /** Variante (uuid) de la imagen. Si viene, los defaults de marca
   *  (colores/fuente) salen de la marca del PRODUCTO de esa imagen
   *  (`variante → producto → brand_id`), no de la marca activa. Fallback a
   *  la marca activa si la imagen no tiene producto/marca. */
  varianteId?: string | null;
  /** Si true, en desktop el sheet ocupa TODO el ancho (left: 0) en vez de
   *  dejar el sidebar a la izquierda. Se usa cuando se abre ARRIBA de un
   *  overlay full-screen que ya tapa el sidebar (ej. el lightbox del
   *  historial): sin esto, a la izquierda del sheet se vería el fondo del
   *  overlay (franja gris) en lugar de la navbar. */
  coverSidebar?: boolean;
}

export const AnuncioFromImageSheet: React.FC<AnuncioFromImageSheetProps> = ({
  open,
  onClose,
  image,
  companyId,
  coverSidebar = false,
  varianteId = null,
}) => {
  const navigate = useNavigate();
  // Ancho del sidebar (desktop) para que el sheet NO tape la navbar: en
  // desktop arranca a la derecha del sidebar; en mobile (sin sidebar) es
  // full-screen. `setMobileBackOverride` engancha el botón "atrás" del
  // header mobile real de la app (mismo que InterfazV2) para que cierre
  // el sheet en vez de navegar.
  const {
    sidebarWidth,
    setMobileBackOverride,
    setMobileTitleOverride,
    mobileTitleOverride,
    mobileBackOverride,
  } = useSidebar();

  // Refs para que el effect sólo dependa de `open` (no re-correr cuando
  // cambia onClose o el título).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const prevTitleRef = useRef<string | null>(null);
  const prevBackRef = useRef<(() => void) | null>(null);

  // Mientras el sheet está abierto: el botón atrás del header mobile de la
  // app cierra el sheet, y el título pasa a "Crear anuncio". Al cerrar se
  // restaura lo previo. (En desktop el header mobile no se renderiza.)
  useEffect(() => {
    if (!open) return;
    prevTitleRef.current = mobileTitleOverride;
    // Guardamos el back override que tenía la pantalla de ABAJO (ej. el
    // lightbox del historial setea `window.history.back()`). Al cerrar el
    // sheet lo RESTAURAMOS en vez de ponerlo en null: sino la pantalla de
    // atrás queda sin botón "volver" (su effect no se re-ejecuta) y el user
    // quedaba atrapado, teniendo que usar la barra inferior.
    prevBackRef.current = mobileBackOverride;
    // Doble flecha: el setter de useState trata una función como updater,
    // así que para GUARDAR un handler hay que devolverlo desde el updater.
    setMobileBackOverride(() => () => onCloseRef.current());
    // El título "Crear anuncio" se muestra en el header mobile de la app
    // (donde normalmente dice "Historial").
    setMobileTitleOverride('Crear anuncio');
    return () => {
      // Restauramos el back previo (no null). El setter trata una función
      // como updater → devolvemos el handler guardado (o null) desde él.
      setMobileBackOverride(() => prevBackRef.current);
      setMobileTitleOverride(prevTitleRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [titulo, setTitulo] = useState<AnuncioFieldState>(FIELD_DEFAULT);
  const [descripcion, setDescripcion] =
    useState<AnuncioFieldState>(FIELD_DEFAULT);
  const [cta, setCta] = useState<AnuncioFieldState>(FIELD_DEFAULT);
  const [colors, setColors] = useState<string[]>([]);
  const [brandColors, setBrandColors] = useState<string[]>([]);
  // Fuente de marca renderizable (default de la tipografía de los 3 textos).
  const [brandFont, setBrandFont] = useState<string>('');
  const [formatoVal, setFormatoVal] = useState<string>('post');
  const [cantidad, setCantidad] = useState<number>(1);
  const [otra, setOtra] = useState<string>('');
  const [formatOpen, setFormatOpen] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Al abrir: reset de la config + cargar colores/fuente de la marca de la
  // IMAGEN (su producto) como default; fallback a la marca activa.
  useEffect(() => {
    if (!open) return;
    setTitulo(FIELD_DEFAULT);
    setDescripcion(FIELD_DEFAULT);
    setCta(FIELD_DEFAULT);
    setColors([]);
    setBrandColors([]);
    setBrandFont('');
    setFormatoVal('post');
    setCantidad(1);
    setOtra('');
    setError(null);
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        // Marca de los defaults: la del PRODUCTO de la imagen (si tenemos su
        // variante), si no la marca activa del perfil. Las imágenes del
        // historial se filtran por `variante → producto → brand_id`; usamos
        // la misma cadena para que el anuncio respete la marca de la imagen.
        let brandId: string | null = null;
        if (varianteId) {
          const { data: v } = await supabase
            .from('variantes')
            .select('productos!inner(brand_id)')
            .eq('id', varianteId)
            .maybeSingle();
          brandId =
            ((v as { productos?: { brand_id?: string } } | null)?.productos
              ?.brand_id as string) || null;
        }
        if (!brandId) {
          const { data: prof } = await supabase
            .from('profiles')
            .select('current_brand_id, id_company')
            .eq('id', user.id)
            .single();
          brandId =
            (prof as { current_brand_id?: string } | null)?.current_brand_id ||
            null;
          // Fallback (ADR-048): cuentas SIN marca activa (`current_brand_id`
          // null — típico de cuentas recién registradas que nunca cambiaron de
          // marca; el switch es lo único que lo escribe) → marca DEFAULT de la
          // empresa. Sin esto, el anuncio desde imagen/historial quedaba SIN
          // colores ni fuente de marca, aunque la marca tuviera todo. Mismo
          // fallback que InterfazV2 / useBrandProfile.
          if (!brandId) {
            const cid =
              (prof as { id_company?: string } | null)?.id_company ||
              companyId ||
              null;
            if (cid) {
              const { data: def } = await supabase
                .from('brand_profiles')
                .select('id')
                .eq('id_company', cid)
                .eq('is_default', true)
                .eq('is_draft', false)
                .maybeSingle();
              brandId = (def as { id?: string } | null)?.id || null;
            }
          }
        }
        if (!brandId || cancelled) return;
        const { data: brand } = await supabase
          .from('brand_profiles')
          .select('colors, fonts')
          .eq('id', brandId)
          .single();
        if (cancelled) return;
        // `colors` llega como `{hex,name,role}[]` (Estudio Virtual) o
        // `string[]`, a veces sin `#`. Normalizamos a `#rrggbb` válidos
        // (sin esto el ColorGrid pintaba todo negro). Mismos helpers que V2.
        setBrandColors(
          normalizeBrandHexColors((brand as { colors?: unknown } | null)?.colors),
        );
        // Fuente de marca por defecto: primera `suggested` renderizable.
        // Sembramos los 3 textos que sigan sin fuente elegida.
        const font = resolveBrandFont(
          (brand as { fonts?: unknown } | null)?.fonts,
        );
        setBrandFont(font);
        if (font) {
          const seed = (s: AnuncioFieldState): AnuncioFieldState =>
            s.font ? s : { ...s, font };
          setTitulo(seed);
          setDescripcion(seed);
          setCta(seed);
        }
      } catch {
        /* noop — sin paleta, el user puede elegir colores a mano */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, varianteId]);

  if (!open || !image) return null;

  const buildTexto = (f: AnuncioFieldState): Record<string, unknown> => {
    if (f.hidden) return { incluir: false };
    const trimmed = f.value.trim();
    const base: Record<string, unknown> = trimmed
      ? { incluir: true, modo: 'custom', texto: trimmed }
      : { incluir: true, modo: 'auto' };
    if (f.font && f.font.trim()) base.fuente = f.font.trim();
    return base;
  };

  const creditCost = Math.max(1, Math.min(AUTO_IMAGE_COUNT_MAX, cantidad));

  const handleGenerate = async () => {
    if (generating || !image) return;
    setError(null);
    setGenerating(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const coloresEfectivos =
        colors.length > 0 ? colors : brandColors.slice(0, ANUNCIO_COLORS_MAX);
      // Correlación para el poll de respaldo. El atlas-backend además lo
      // EXIGE (uuid, es su contrato de polling); n8n lo ignora.
      const executionId = crypto.randomUUID();
      const req: Record<string, unknown> = {
        user_id: user.id,
        modo: 'anuncio_desde_imagen',
        // La base del anuncio es la imagen generada, no un producto.
        imagen_generada_id: image.id,
        cantidad_imagenes: creditCost,
        Format: formatSlugToAspect(formatoVal),
        textos: {
          titulo: buildTexto(titulo),
          descripcion: buildTexto(descripcion),
          cta: buildTexto(cta),
        },
        ...(coloresEfectivos.length > 0 ? { colores: coloresEfectivos } : {}),
        ...(otra.trim() ? { instruccion_adicional: otra.trim() } : {}),
        new_ui: true,
        modo_pro: true,
        execution_id: executionId,
      };
      // Carrera respuesta-vs-poll (mismo esquema que el modo anuncio de
      // InterfazV2): gpt-5 en high puede superar el wait de 150s del
      // atlas-backend — ahí responde 202 sin ids y el poll por execution_id
      // termina el trabajo (el backend escribe una fila por imagen).
      const result = await awaitImageGeneration({
        fetchPromise: postGenerateAd(req, { url: GENERATE_AD_V3_URL }),
        executionId,
        companyId,
        expectedCount: creditCost,
        // gpt-5 en high tarda minutos POR imagen — ventanas de anuncio
        // (ADR-578) para que el poll no cierre la tanda incompleta.
        pollTimeoutMs: ANUNCIO_POLL_TIMEOUT_MS,
        pollStableRounds: ANUNCIO_STABLE_ROUNDS,
      });
      const imageIds = result.imageIds;
      if (imageIds.length > 0) {
        const companyIdResult = result.companyId || companyId;
        let preloadedImages: unknown[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(
            imageIds,
            companyIdResult,
          );
        } catch {
          /* noop — el preload es best-effort */
        }
        sonarListo();
        onClose();
        // Navegamos a la pantalla de resultados con los nuevos ids. El `key`
        // en el state fuerza que la página tome la nueva tanda aunque ya
        // estemos en esa ruta.
        navigate('/redes-sociales/imagenes-generadas', {
          state: {
            imageIds,
            companyId: companyIdResult,
            preloadedImages,
            ts: Date.now(),
          },
        });
      } else {
        throw new Error('No se generaron imágenes');
      }
    } catch (e) {
      console.error('[AnuncioFromImage] generación falló', e);
      // Errores accionables (sin créditos / política de contenido): mostrar
      // el mensaje real — reintentar igual no va a funcionar.
      setError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'Se produjo un error al generar el anuncio. Volvé a intentarlo.',
      );
    } finally {
      setGenerating(false);
    }
  };

  return createPortal(
    <div
      // En mobile el z queda por DEBAJO del header de la app (z-50) para que
      // se vea su top bar real (back + créditos + menú), igual que en
      // InterfazV2. En desktop no hay header mobile, así que sube a z-[120].
      // El FormatPicker y el LoadingAnimation usan su propio portal (z alto),
      // así que no quedan capados por este stacking context.
      className="fixed inset-0 z-40 md:z-[120] bg-white md:bg-[#EFF0F5] flex flex-col md:left-[var(--anuncio-sb)]"
      style={
        {
          // `coverSidebar`: el sheet se abre arriba de un overlay full-screen
          // (lightbox del historial) que ya tapa el sidebar → ocupamos todo
          // el ancho (left: 0) para no dejar una franja gris a la izquierda.
          ['--anuncio-sb' as string]: coverSidebar ? '0px' : `${sidebarWidth}px`,
        } as React.CSSProperties
      }
    >
      {/* DESKTOP header: Volver (izq) + Generar flotante (der). En mobile el
          back flota sobre la imagen y el Generar es sticky al pie. */}
      <header className="hidden md:flex shrink-0 items-center justify-between gap-3 px-6 pt-4 pb-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity px-3 py-1.5 rounded-full bg-white/85 backdrop-blur-md shadow-sm ring-1 ring-black/[0.04]"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 py-2.5 pl-5 pr-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors disabled:opacity-60"
        >
          <Sparkles className="w-4 h-4" />
          <span>Generar</span>
          <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-3.5 h-3.5 object-contain"
            />
            {creditCost}
          </span>
        </button>
      </header>

      {/* Body scrolleable. Desktop = fila (imagen izq + opciones der).
          Mobile = columna: imagen full-bleed arriba del todo + opciones. */}
      <div className="flex-1 min-h-0 overflow-y-auto pt-[calc(env(safe-area-inset-top,0px)+68px)] md:pt-0 md:px-6 md:pb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-center md:gap-6 md:max-w-[1200px] md:mx-auto">
          {/* Columna de la imagen. Mobile: la imagen sola (sin card) + una
              descripción de la pantalla debajo. Desktop: sólo la imagen,
              sticky a la izquierda. */}
          <div className="relative px-4 md:px-0 md:flex-1 md:max-w-[620px] md:sticky md:top-0 flex flex-col items-center justify-center">
            <img
              src={image.url}
              alt="Imagen de referencia del anuncio"
              className="w-full md:w-auto max-w-full md:max-h-[calc(100vh-9rem)] md:object-contain rounded-xl md:rounded-2xl"
            />
            {/* Mobile: explicación de la pantalla debajo de la imagen. */}
            <p className="md:hidden mt-3 text-[13.5px] text-neutral-500 leading-snug text-center">
              Convertí esta imagen en un anuncio listo para publicar. Elegí los
              textos, colores y formato, y Atlas armará el diseño por vos.
            </p>
          </div>

          {/* Opciones — reusa AnuncioPanel SIN la sección Productos. */}
          <div className="w-full md:w-[440px] md:shrink-0 px-4 md:px-0 pt-4 md:pt-0 pb-4 md:pb-0">
            {/* Desktop: título + descripción arriba del container de textos.
                En mobile el título va en el header de la app y la descripción
                debajo de la imagen. */}
            <h1 className="hidden md:block text-[26px] font-semibold text-brand-black/90 leading-tight">
              Crear anuncio
            </h1>
            <p className="hidden md:block mb-4 mt-1.5 text-[14px] text-neutral-500 leading-snug">
              Convertí esta imagen en un anuncio listo para publicar. Elegí los
              textos, colores y formato, y Atlas armará el diseño por vos.
            </p>
            {error && (
              <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-[13px] text-red-700">
                {error}
              </div>
            )}
            <AnuncioPanel
              variant="desktop"
              titulo={titulo}
              onTituloChange={setTitulo}
              descripcion={descripcion}
              onDescripcionChange={setDescripcion}
              cta={cta}
              onCtaChange={setCta}
              colors={colors}
              onColorsChange={setColors}
              brandColors={brandColors}
              autoImageCount={cantidad}
              onAutoImageCountChange={setCantidad}
              autoImageCountMax={AUTO_IMAGE_COUNT_MAX}
              formatoLabel={getFormatLabel(formatoVal)}
              onOpenFormat={() => setFormatOpen(true)}
              otraConfig={otra}
              onOtraChange={setOtra}
              minimalHeaders="always"
            />
          </div>
        </div>
      </div>

      {/* MOBILE: Generar sticky al pie (en desktop va en el header). Mismo
          estilo que el botón Generar del modo anuncio mobile. */}
      <div
        className="md:hidden shrink-0 px-4 pt-3 bg-white/95 backdrop-blur-sm border-t border-black/[0.05]"
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors disabled:opacity-60"
        >
          <Sparkles className="w-5 h-5" />
          <span>Generar</span>
          <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-3.5 h-3.5 object-contain"
            />
            {creditCost}
          </span>
        </button>
      </div>

      <FormatPicker
        open={formatOpen}
        selectedId={formatoVal}
        onChange={(id) => setFormatoVal(id)}
        onClose={() => setFormatOpen(false)}
      />

      {generating && (
        <LoadingAnimation
          message="Generando anuncio..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={210_000}
        />
      )}
    </div>,
    document.body,
  );
};

export default AnuncioFromImageSheet;
